/**
 * Capacitor Notification Adapter
 * Uses Firebase Cloud Messaging (FCM) for Android push notifications
 *
 * Requires @capacitor-firebase/messaging to be installed when using Capacitor.
 * This module uses dynamic imports so the dependency is optional at build time.
 */

import type { NotificationAdapter } from './notification.adapter';
import { apiClient } from '../api.client';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function getFirebaseMessaging(): Promise<any> {
  try {
    // Dynamic import — @capacitor-firebase/messaging is only available on Capacitor builds
    const moduleName = '@capacitor-firebase/messaging';
    return await (Function('m', 'return import(m)')(moduleName));
  } catch {
    console.warn('Capacitor Firebase Messaging not available');
    return null;
  }
}

export class CapacitorNotificationAdapter implements NotificationAdapter {
  private fcmToken: string | null = null;

  isSupported(): boolean {
    return !!window.Capacitor?.isNativePlatform();
  }

  async requestPermission(): Promise<boolean> {
    try {
      const mod = await getFirebaseMessaging();
      if (!mod) return false;
      const result = await mod.FirebaseMessaging.requestPermissions();
      return result.receive === 'granted';
    } catch {
      return false;
    }
  }

  async subscribe(): Promise<boolean> {
    if (!this.isSupported()) return false;

    try {
      const permissionGranted = await this.requestPermission();
      if (!permissionGranted) return false;

      const mod = await getFirebaseMessaging();
      if (!mod) return false;

      // Get FCM token
      const { token } = await mod.FirebaseMessaging.getToken();
      this.fcmToken = token;

      // Send to backend
      await apiClient.post('/push/subscribe', {
        fcm_token: token,
        device_type: 'android',
        platform: 'capacitor',
      });

      // Listen for token refresh
      mod.FirebaseMessaging.addListener('tokenReceived', async (event: { token: string }) => {
        this.fcmToken = event.token;
        await apiClient.post('/push/subscribe', {
          fcm_token: event.token,
          device_type: 'android',
          platform: 'capacitor',
        }).catch(() => {});
      });

      return true;
    } catch (error) {
      console.error('Failed to subscribe to FCM:', error);
      return false;
    }
  }

  async unsubscribe(): Promise<boolean> {
    if (!this.fcmToken) return true;

    try {
      await apiClient.delete('/push/unsubscribe', {
        data: { endpoint: this.fcmToken },
      });

      const mod = await getFirebaseMessaging();
      if (mod) {
        await mod.FirebaseMessaging.deleteToken();
      }
      this.fcmToken = null;

      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from FCM:', error);
      return false;
    }
  }

  async isSubscribed(): Promise<boolean> {
    // Check in-memory token first (fast path)
    if (this.fcmToken) return true;

    // After app restart, fcmToken is lost — check FCM directly
    try {
      const mod = await getFirebaseMessaging();
      if (!mod) return false;
      const { token } = await mod.FirebaseMessaging.getToken();
      if (token) {
        this.fcmToken = token;
        return true;
      }
    } catch {
      // No token available — not subscribed
    }
    return false;
  }
}
